import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Conversation from 'components/common/Conversation';
import type { FC } from 'react';
import { useState } from 'react';
import { useGetBusinessUnitQuery } from 'services/api/businessUnits.api';
import { useGetClientQuery } from 'services/api/clients.api';
import { useGetClientMessagesQuery } from 'services/api/messages';

import ClientInfoPanel from '../../Client/ClientInfoPanel';

interface ChatPanelProps {
  clientId: number;
  onClose: () => void;
}

const chatSurfaceSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
  backgroundImage: 'url("/whatsapp-background.png")',
  backgroundColor: '#efeae2',
  backgroundRepeat: 'repeat',
  backgroundSize: '412.5px 749.25px',
} as const;

const ChatPanel: FC<ChatPanelProps> = ({ clientId, onClose }) => {
  const { data: client, isLoading: isClientLoading } = useGetClientQuery(String(clientId), {
    refetchOnMountOrArgChange: true,
  });
  const { data: messages, isLoading: isMessagesLoading } = useGetClientMessagesQuery(String(clientId), {
    refetchOnMountOrArgChange: true,
  });

  const { data: businessUnit } = useGetBusinessUnitQuery(client?.businessUnitId ?? 0, {
    skip: !client?.businessUnitId,
  });

  const clientName =
    client?.firstName && client?.lastName ? `${client.firstName} ${client.lastName}` : client?.phone || 'Cliente';

  const isLoading = isClientLoading || isMessagesLoading;

  const [infoExpanded, setInfoExpanded] = useState(true);

  const handleOpenFull = () => {
    window.open(`/clients/${clientId}`, '_blank');
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '10vh',
        bottom: 0,
        right: 0,
        width: 420,
        backgroundColor: '#ffffff',
        borderRadius: '12px 0 0 12px',
        boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          backgroundColor: '#15183A',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#ffffff',
            fontWeight: 700,
            fontFamily: 'Jost',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isClientLoading ? 'Cargando...' : clientName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleOpenFull}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ffffff' } }}
          >
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#ffffff' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Body: info section stacked above chat */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Info section */}
        <Box sx={{ flexShrink: 0, backgroundColor: '#ffffff', borderBottom: '1px solid rgba(21, 24, 58, 0.08)' }}>
          {/* Collapsible content */}
          {infoExpanded && (
            <Box sx={{ maxHeight: 480, overflowY: 'auto', px: 1, pt: 0.5 }}>
              {isClientLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} sx={{ color: '#15183A' }} />
                </Box>
              ) : client ? (
                <ClientInfoPanel
                  client={client}
                  businessUnit={businessUnit}
                  isCompact={false}
                  allowedSectionIds={['main-info']}
                  disableRootScroll
                  hideSectionTitles
                />
              ) : null}
            </Box>
          )}

          {/* Toggle button */}
          <Box
            onClick={() => setInfoExpanded((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              height: 32,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(21, 24, 58, 0.04)' },
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontFamily: 'Jost', fontWeight: 600, color: 'rgba(21,24,58,0.5)', fontSize: '0.7rem' }}
            >
              {infoExpanded ? 'Ocultar' : 'Ver información'}
            </Typography>
            {infoExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 14, color: 'rgba(21,24,58,0.5)' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 14, color: 'rgba(21,24,58,0.5)' }} />
            )}
          </Box>
        </Box>

        {/* Chat surface */}
        <Box sx={{ ...chatSurfaceSx, flex: 1, minHeight: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={32} sx={{ color: '#15183A' }} />
            </Box>
          ) : (
            <Conversation conversation={messages || []} isFilteredConversation compact fullHeight />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatPanel;
